import autobind from 'core-decorators/lib/autobind';
import { connect } from 'react-redux';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import i18n from '@wix/wix-vod-shared/src/common/i18n';

import { isNeverShow } from 'widget/utils/video-list';
import { isSignInVisible } from 'shared/utils/layout-helpers';
import { mainItemIsChannelCover } from '@wix/wix-vod-shared/src/common/utils/overlay-buttons-utils';
import { hasVideos } from 'shared/utils/channel-helpers';

import { openSubscribeOverlay } from 'widget/redux/client/actions/mobile-overlay-openners/payment-pages-actions';
import { visitChannel } from 'widget/redux/client/actions/mobile-overlay-openners/main-openners';

import { getChannelForWidget } from '@wix/wix-vod-shared/src/common/selectors/channels';
import { getCurrentSiteUser } from 'widget/selectors/current-site-user';
import {
  getAllSettings,
  canShowMainItemTitle,
  areElementsRounded,
  canShowVideoList,
  isCompactLayout,
  isStripLayout,
  isSingleVideo,
} from 'shared/selectors/app-settings';

import ComingSoon from 'widget/components/player-overlay/partials/coming-soon';
import ClampText from '@wix/wix-vod-shared/src/common/components/clamp-text/clamp-text';
import { withBiEvent } from 'shared/bi/send-bi-event';

import LiveLabel from 'widget/containers/live-label/live-label';
import { PlayButton, PurchaseButton } from './buttons/middle-buttons';
import { BottomButton, SubscribeBottomButton } from './buttons/bottom-buttons';
import BaseOverlay from 'widget/components/player-overlay/_base-overlay';
import renderVideoPlayButtonText from 'widget/components/overlay-texts/video/play-button-text';
import renderVideoSignInToWatchButtonText from 'widget/components/overlay-texts/video/sign-in-to-watch-button-text';
import renderChannelPlayButtonText from 'widget/components/overlay-texts/channel/play-button-text';
import renderChannelSignInToWatchButtonText from 'widget/components/overlay-texts/channel/sign-in-to-watch-button-text';
import renderOpenVideoWithoutPreviewButtonText from 'widget/components/overlay-texts/channel/open-video-without-preview-button-text';
import renderPurchaseButtonText from 'widget/components/overlay-texts/purchase-button-text';
import renderChannelSubscribeButtonText, { isVisible as isChannelSubscribeButtonVisible } from 'widget/components/overlay-texts/channel/subscribe-button-text';

import styles from './three-rows-widget.styl';

import translations from 'messages/widget';

const WIDGET_PADDING = 30;
const MARGIN_BETWEEN_BOTTOM_BUTTONS = 20;
const BOTTOM_BUTTON_PADDING = 16;
const MAX_SINGLE_BUTTON_CONTENT_WIDTH = window.innerWidth - 2 * WIDGET_PADDING - 2 * BOTTOM_BUTTON_PADDING;
const MAX_TWO_BUTTONS_CONTENT_WIDTH = MAX_SINGLE_BUTTON_CONTENT_WIDTH - MARGIN_BETWEEN_BOTTOM_BUTTONS;


@connect(state => {
  const channelData = getChannelForWidget(state);
  const appSettings = getAllSettings(state);
  const isVideoListHiddenInStrip = isStripLayout(state) && isNeverShow(state.appSettings.numbers.videoListVisibility);
  const isVideoListHiddenInCompact = isCompactLayout(state) && !canShowVideoList(state);

  return {
    channelData,
    appSettings,
    isChannelCover: mainItemIsChannelCover(appSettings),
    isSignInVisible: isSignInVisible(appSettings),
    isVideoListHidden: isVideoListHiddenInStrip || isVideoListHiddenInCompact,
    canShowMainItemTitle: canShowMainItemTitle(state),
    isSingleVideoAtChannel: channelData.videosCount === 1 || isSingleVideo(state),
    currentSiteUser: getCurrentSiteUser(state),
    isRounded: areElementsRounded(state),
    isSingleVideo: isSingleVideo(state),
  };
}, {
  onVisitChannelClick: visitChannel,
  onSubscribeClick: openSubscribeOverlay,
})
export default class ThreeRowsWidgetOverlay extends BaseOverlay {

  static displayName = 'ThreeRowsWidgetOverlay';

  static propTypes = {
    ...BaseOverlay.propTypes,
    onSubscribeClick: PropTypes.func,
    onVisitChannelClick: PropTypes.func,
    onBuyVideoClick: PropTypes.func,
    onPlayClick: PropTypes.func,
    onOpenVideoClick: PropTypes.func,
    onSignInRequested: PropTypes.func.isRequired,
    bottomButtons: PropTypes.array,
    middleButton: PropTypes.object,

    isChannelCover: PropTypes.bool,
    isSignInVisible: PropTypes.bool,
    isVideoListHidden: PropTypes.bool,
    canShowMainItemTitle: PropTypes.bool,
    isSingleVideoAtChannel: PropTypes.bool,
    isRounded: PropTypes.bool,
    isSingleVideo: PropTypes.bool,
  };

  static defaultProps = {
    onSubscribeClick: _.noop,
    onVisitChannelClick: _.noop,
  };

  get canOpenChannelPage() {
    const { isSingleVideoAtChannel } = this.props;

    return this.isChannelCoverView && !isSingleVideoAtChannel;
  }

  renderTitle() {
    const { onOpenVideoClick, onVisitChannelClick } = this.props;

    return (
      <ClampText className={styles['title-text']}
                 text={this.titleText}
                 onClick={this.canOpenChannelPage ? onVisitChannelClick : onOpenVideoClick}
                 role="button"
                 dataHook="title" />
    );
  }

  renderLiveLabel() {
    const { videoItem } = this.props;

    return (
      <LiveLabel itemType={videoItem.itemType}
                 videoTitle={videoItem.title}
                 startTime={videoItem.dateStartLive}
                 className={styles['live-label']} />
    );
  }

  renderPlayButton() {
    const { channelData, isSingleVideo } = this.props;
    if (!hasVideos(channelData) && !isSingleVideo) {
      return (
        <ComingSoon className={styles['soon-info']} />
      );
    }

    const text = this.isChannelCoverView ?
      renderChannelPlayButtonText(this.buttonTextProps) :
      renderVideoPlayButtonText(this.buttonTextProps);

    if (!text) {
      return null;
    }

    return (
      <PlayButton onClick={this.props.onPlayClick}>
        {text}
      </PlayButton>
    );
  }

  renderSignInToWatchButton() {
    const text = this.isChannelCoverView ?
      renderChannelSignInToWatchButtonText(this.buttonTextProps) :
      renderVideoSignInToWatchButtonText(this.buttonTextProps);

    if (!text) {
      return null;
    }

    return (
      <PlayButton onClick={this.props.onPlayClick}>
        {text}
      </PlayButton>
    );
  }

  renderOpenVideoButton() {
    if (!this.isChannelCoverView) {
      return null;
    }

    const text = renderOpenVideoWithoutPreviewButtonText(this.buttonTextProps);

    if (!text) {
      return null;
    }

    return (
      <PlayButton onClick={this.props.onOpenVideoClick}>
        {text}
      </PlayButton>
    );
  }

  renderPurchaseButton() {
    const text = renderPurchaseButtonText(this.buttonTextProps);

    if (!text) {
      return null;
    }

    return (
      <PurchaseButton onClick={this.props.onBuyVideoClick}>
        {text}
      </PurchaseButton>
    );
  }

  renderSignInButton() {
    const { isSignInVisible, currentSiteUser, onSignInRequested } = this.props;

    if (!isSignInVisible || currentSiteUser) {
      return null;
    }

    return (
      <BottomButton className={styles['bottom-button']}
                    dataHook="mobileSignInButton"
                    key="sign-in-button"
                    onClick={onSignInRequested}>
        {i18n.t(translations.mobileSignIn)}
      </BottomButton>
    );
  }

  @autobind
  @withBiEvent('widget.exploreThisChannel.clicked')
  handleVisitChannelClick() {
    this.props.onVisitChannelClick();
  }

  get visitChannelButton() {
    return (
      <BottomButton className={styles['bottom-button']}
                    key="visit-channel-button"
                    onClick={this.handleVisitChannelClick}>
        {i18n.t(translations.viewChannel)}
      </BottomButton>
    );
  }

  renderSubscribeButton(hasButtonBeside = false) {
    const text = renderChannelSubscribeButtonText(this.buttonTextProps);

    if (!text) {
      return null;
    }

    const maxWidth = hasButtonBeside ? MAX_TWO_BUTTONS_CONTENT_WIDTH : MAX_SINGLE_BUTTON_CONTENT_WIDTH;

    return (
      <SubscribeBottomButton className={styles['bottom-button']}
                             key="subscribe-button"
                             forceShortText={hasButtonBeside}
                             maxWidth={maxWidth}
                             onClick={this.props.onSubscribeClick}>
        {text}
      </SubscribeBottomButton>
    );
  }

  renderBottomButtons() {
    const _isChannelSubscribeButtonVisible = isChannelSubscribeButtonVisible(this.buttonTextProps);
    const { isVideoListHidden, isSingleVideoAtChannel } = this.props;

    if (isVideoListHidden || isSingleVideoAtChannel) {
      const signInButton = this.renderSignInButton();

      if (!signInButton && !_isChannelSubscribeButtonVisible) {
        return [];
      }

      return _.compact([
        signInButton,
        this.renderSubscribeButton(Boolean(signInButton)),
      ]);
    }

    const visitChannelButton = this.visitChannelButton;

    if (!visitChannelButton && !_isChannelSubscribeButtonVisible) {
      return [];
    }

    return _.compact([
      visitChannelButton,
      this.renderSubscribeButton(Boolean(visitChannelButton)),
    ]);
  }

  renderBottomButtonsBlock() {
    const bottomButtons = this.renderBottomButtons();

    if (_.isEmpty(bottomButtons)) {
      return null;
    }

    const { isRounded } = this.props;

    const className = classnames(styles.buttons, {
      [styles.single]: bottomButtons.length === 1,
      [styles.rounded]: isRounded,
    });

    return (
      <div className={className}>
        {bottomButtons}
      </div>
    );
  }

  render() {
    const { className } = this.props;

    return (
      <section className={classnames(className, styles.overlay)}
               aria-label={this.titleText}>
        <div className={styles.main}
             role="presentation">
          <div className={styles.title}
               role="presentation">
            {this.shouldShowTitle && this.renderTitle()}
            {this.renderLiveLabel()}
          </div>

          <div className={styles['play-container']}
               role="presentation">
            {
              this.renderPlayButton() ||
              this.renderSignInToWatchButton() ||
              this.renderOpenVideoButton() ||
              this.renderPurchaseButton()
            }
          </div>
        </div>

        {this.renderBottomButtonsBlock()}
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/components/player-overlay/mobile/three-rows-widget/three-rows-widget.js